import { createContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import qs from 'qs';

import axiosToLive from '../utils/api';
import axiosToQBConnect from '../utils/qbconnect';
import { checkAccessToLive } from '../utils/jwt';

const AuthContext = createContext();

const STORAGE_AUTH_KEY = 'auth';

const AuthProvider = ({ children }) => {
	const i18n = useTranslation();

	const { enqueueSnackbar } = useSnackbar();

	const [credentials, setCredentials] = useState({
		user: localStorage.getItem(STORAGE_AUTH_KEY)
			? JSON.parse(localStorage.getItem(STORAGE_AUTH_KEY)).user
			: null,
		token: localStorage.getItem(STORAGE_AUTH_KEY)
			? JSON.parse(localStorage.getItem(STORAGE_AUTH_KEY)).token
			: null,
	});

	useEffect(() => {
		if (credentials !== null) {
			localStorage.setItem(STORAGE_AUTH_KEY, JSON.stringify(credentials));
		}
	}, [credentials]);

	useEffect(() => {
		if (credentials.token !== null) {
			const myInterceptor = axiosToLive.interceptors.request.use(
				async (config) => {
					config.headers = {
						authorization: `Bearer ${credentials.token}`,
					};

					return config;
				},
				(error) => {
					if (error.response.status === 401) {
						if (credentials.refresh_token !== null) {
							refreshToken(credentials.refresh_token);
						} else {
							enqueueSnackbar(`${i18n.t('nonValidSession')}`, {
								variant: 'warning',
							});
							setTimeout(() => {
								setCredentials({
									user: null,
									token: null,
									refresh_token: null,
								});
							}, 2000);
						}
					}
				}
			);

			return () => axiosToLive.interceptors.request.eject(myInterceptor);
		}
	}, [credentials.token, enqueueSnackbar, i18n]);

	const signIn = async (values) => {
		const data = qs.stringify({
			email: values.email,
			pwd: values.password,
		});

		const response = await axiosToQBConnect.post('/token', data, {
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
		});

		if (response.status !== 200) {
			signOut();
			throw new Error();
		}

		if (!checkAccessToLive(response.data.access_token)) {
			signOut();
			throw new Error(`${i18n.t('errorBadAccountRight')}`);
		}

		setCredentials({
			user: values.email,
			token: response.data.access_token,
			refresh_token: response.data.refresh_token,
		});

		return response;
	};

	const signOut = async () => {
		setCredentials({
			user: null,
			token: null,
			refresh_token: null,
		});
	};

	const refreshToken = async (refreshToken) => {
		const data = qs.stringify({
			refresh_token: refreshToken,
		});

		axiosToQBConnect
			.post('/refresh', data, {
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
			})
			.then((response) => {
				setCredentials((old) => ({
					...old,
					token: response.data.access_token,
					refresh_token: response.data.refresh_token,
				}));
			})
			.catch(() => {
				signOut();
			});
	};

	return (
		<AuthContext.Provider
			value={{
				isSigned: credentials.token !== null && credentials.token !== undefined,
				credentials: credentials,
				signIn,
				signOut,
				refreshToken,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export { AuthContext, AuthProvider };
